import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import Masonry from 'react-masonry-css';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import Lenis from 'lenis';
import './App.css';
import './animations.css';

// Import all components
import Navigation from './components/Navigation';
import Hero from './components/Hero';
import About from './components/About';
import Weddings from './components/Weddings';
import Banquets from './components/Banquets';
import Services from './components/Services';
import Gallery from './components/Gallery';
import Testimonials from './components/Testimonials';
import Reviews from './components/Reviews';
import Press from './components/Press';
import CTA from './components/CTA';
import Footer from './components/Footer';
import ScrollProgress from './components/ScrollProgress';
import Destinations from './components/Destinations';
import StoriesAndStats from './components/StoriesAndStats';
import SupportBar from './components/SupportBar';
import DestinationsMarquee from './components/DestinationsMarquee';
import Gifting from './components/Gifting';
import Portfolio from './components/Portfolio';
import Studio from './components/Studio';
import Contact from './components/Contact';
import CSR from './components/CSR';
import { useLocation } from 'react-router-dom';
import HomePreloader from './components/HomePreloader';

const images = [
  'i1.jpg', 'i10.jpg', 'i11.jpg', 'i12.jpg', 'i13.jpg', 'i14.jpg', 'i15.jpg', 'i16.jpg', 'i17.jpg', 'i18.jpg', 'i19.jpg', 'i2.jpg', 'i20.jpg', 'i21.jpg', 'i22.jpg', 'i23.jpg', 'i24.jpg', 'i25.jpg', 'i26.jpg', 'i27.jpg', 'i28.jpg', 'i29.jpg', 'i3.png', 'i30.jpg', 'i31.jpg', 'i32.jpg', 'i33.jpg', 'i34.jpg', 'i35.jpg', 'i36.jpg', 'i37.jpg', 'i38.jpg', 'i39.jpg', 'i4.jpg', 'i40.jpg', 'i41.jpeg', 'i5.jpg', 'i6.jpg', 'i7.jpg', 'i8.jpg', 'i9.jpg'
].map(image => ({
  src: `/img/${image}`,
  alt: image,
}));

const breakpointColumnsObj = {
  default: 3,
  1100: 3,
  768: 2,
  500: 1
};

const App: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [homePreloaderDone, setHomePreloaderDone] = useState(false);
  const location = useLocation();
  const homeRef = useRef<HTMLDivElement>(null);
  const aboutRef = useRef<HTMLDivElement>(null);
  const collectionsRef = useRef<HTMLDivElement>(null);
  const galleryRef = useRef<HTMLDivElement>(null);
  const contactRef = useRef<HTMLDivElement>(null);

  const scrollToRef = (ref: React.RefObject<HTMLDivElement | null>) => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth' });
    }
    setMenuOpen(false);
  };

  const openLightbox = useCallback((index: number) => {
    setIndex(index);
    setOpen(true);
  }, []);

  // Lenis smooth scroll setup
  useEffect(() => {
    const lenis = new Lenis();

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  useEffect(() => {
    if (menuOpen) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
  }, [menuOpen]);

  // Subtle scroll-reveal animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const target = entry.target as HTMLElement;
          if (entry.isIntersecting) {
            target.classList.add('in-view');
          } else {
            target.classList.remove('in-view');
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    );

    const revealEls = document.querySelectorAll('.reveal');
    revealEls.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <motion.div
      className={`App ${menuOpen ? 'menu-open' : ''}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <ScrollProgress />
      <Navigation />
      <SupportBar />

      <main className="main-content">
        {location.pathname === '/about' ? (
          <>
            <About />
            <Footer />
          </>
        ) : location.pathname === '/weddings' ? (
          <>
            <Weddings />
            <Footer />
          </>
        ) : location.pathname === '/banquets' ? (
          <>
            <Banquets />
            <Footer />
          </>
        ) : location.pathname === '/gifting' ? (
          <>
            <Gifting />
            <Footer />
          </>
        ) : location.pathname === '/portfolio' ? (
          <>
            <Portfolio />
            <Footer />
          </>
        ) : location.pathname === '/studio' ? (
          <>
            <Studio />
            <Footer />
          </>
        ) : location.pathname === '/contact' ? (
          <>
            <Contact />
            <Footer />
          </>
        ) : location.pathname === '/csr' ? (
          <>
            <CSR />
            <Footer />
          </>
        ) : (
          <>
            {/* Home-only preloader overlay */}
            <HomePreloader onDone={() => setHomePreloaderDone(true)} />
            <motion.div
              ref={homeRef}
              initial={{ opacity: 0, y: 100 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 1.2,
                delay: 0.3,
                ease: [0.25, 0.46, 0.45, 0.94]
              }}
            >
              <Hero />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 80 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                duration: 1.0,
                delay: 0.2,
                ease: [0.25, 0.46, 0.45, 0.94]
              }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <Destinations />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 80 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                duration: 1.0,
                delay: 0.2,
                ease: [0.25, 0.46, 0.45, 0.94]
              }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <StoriesAndStats />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 80, rotateX: 15 }}
              whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
              transition={{
                duration: 1.2,
                delay: 0.1,
                ease: [0.25, 0.46, 0.45, 0.94]
              }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <Services />
            </motion.div>

            <motion.div
              ref={galleryRef}
              initial={{ opacity: 0, y: 100, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{
                duration: 1.4,
                ease: [0.25, 0.46, 0.45, 0.94]
              }}
              viewport={{ once: true, margin: "-150px" }}
            >
              <Gallery />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: -50, y: 50 }}
              whileInView={{ opacity: 1, x: 0, y: 0 }}
              transition={{
                duration: 1.0,
                delay: 0.3,
                ease: [0.25, 0.46, 0.45, 0.94]
              }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <Reviews />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 80, scale: 0.95 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              transition={{
                duration: 1.0,
                delay: 0.4,
                ease: [0.25, 0.46, 0.45, 0.94]
              }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <Press />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 80, rotateY: 10 }}
              whileInView={{ opacity: 1, y: 0, rotateY: 0 }}
              transition={{
                duration: 1.2,
                delay: 0.2,
                ease: [0.25, 0.46, 0.45, 0.94]
              }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <Testimonials />
            </motion.div>

            {/* Destinations marquee placed before CTA */}
            <motion.div
              initial={{ opacity: 0, y: 60 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.0, ease: [0.25, 0.46, 0.45, 0.94] }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <DestinationsMarquee />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 100, rotateX: 20 }}
              whileInView={{ opacity: 1, y: 0, rotateX: 0 }}
              transition={{
                duration: 1.3,
                delay: 0.1,
                ease: [0.25, 0.46, 0.45, 0.94]
              }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <CTA />
            </motion.div>

            <motion.div
              ref={contactRef}
              initial={{ opacity: 0, y: 80 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{
                duration: 1.0,
                delay: 0.2,
                ease: [0.25, 0.46, 0.45, 0.94]
              }}
              viewport={{ once: true, margin: "-100px" }}
            >
              <Footer />
            </motion.div>
          </>
        )}
      </main>
    </motion.div>
  );
};

export default App;

